/**
 * SNS 共有・スコア画像生成を一元管理。
 *
 * - Web Share API が使える場合はネイティブ共有
 * - 使えない場合は Twitter / LINE / Facebook / コピー の個別ボタンを表示
 * - Canvas でスコアカード画像を生成してダウンロード可能
 */

import { isNativeApp, publicHost, publicOrigin } from '../platform/native';
import { nativeShareFile, nativeShareText } from '../platform/nativeShare';
import type { StageDefinition } from '../game/config/types';
import { renderShareCard, type ShareVisual } from './ShareCard';
import { formatDailyRank } from '../meta/dailyRanking';

export interface ShareableResult {
  score: number;
  highScore: number;
  isNewRecord: boolean;
  maxLevelReached: number;
  maxItemName: string;
  stageTitle: string;
  /** 「今日の県」で遊んだとき（例: 今日の県 9/24）。 */
  dailyLabel?: string;
  /** 「今日の県」の日付（YYYY-MM-DD）。共有リンクに ?daily= を付け、同じ順番で挑戦してもらう。 */
  dailyDate?: string;
  /** 県名（今日の県の挑戦文に使う）。 */
  regionName?: string;
  /** 今日の県の順位（サーバーから取れたときだけ。結果画面を出したあとに書き足される）。 */
  dailyRank?: { rank: number; total: number };
  /** このプレイの最大コンボ（2 以上のときだけ文面に入れる）。 */
  maxCombo?: number;
}

/** 文面の「最大コンボ: 5」の行（コンボしていなければ空）。 */
function comboLine(result: Pick<ShareableResult, 'maxCombo'>): string {
  return result.maxCombo && result.maxCombo >= 2 ? `最大コンボ: ${result.maxCombo}\n` : '';
}

/**
 * ハッシュタグはステージ名から作る。
 * 直接書くと、県を足したときに北海道でも「#福岡めしマージ」が付いてしまう。
 */
function hashtag(stageTitle: string): string {
  return `#${stageTitle.replace(/\s+/g, '')}`;
}

/** 全国共通のタグ。県別のタグだけだと47県ぶんに投稿が散ってしまうので併記する。 */
const NATIONAL_TAG = '#ご当地めしマージ';

export function hashtags(stageTitle: string): string {
  const local = hashtag(stageTitle);
  return local === NATIONAL_TAG ? local : `${local} ${NATIONAL_TAG}`;
}

/** 共有の手段（共有リンクの utm_medium。計測の流入元に出る）。 */
export type ShareMedium = 'x' | 'line' | 'copy' | 'native' | 'image_share' | 'facebook';

/**
 * 共有する URL。`?platform=mock` などのクエリやハッシュは付けない。
 * 今日の県の結果だけは `?daily=<日付>` を付ける（開いた人も同じ順番で遊べる）。
 * 手段（medium）を渡すと `utm_source=share&utm_medium=<手段>` を付ける（Web 版の起動の記録に入る）。
 */
export function shareUrl(result?: Pick<ShareableResult, 'dailyDate'>, medium?: ShareMedium): string {
  // アプリの中の住所（capacitor://localhost）は友だちが開けないので、本番の Web 版にする
  const base = `${publicOrigin()}${location.pathname}`;
  const params: string[] = [];
  if (result?.dailyDate) params.push(`daily=${result.dailyDate}`);
  if (medium) params.push('utm_source=share', `utm_medium=${medium}`);
  return params.length > 0 ? `${base}?${params.join('&')}` : base;
}

export function buildShareText(result: ShareableResult, medium?: ShareMedium): string {
  if (result.dailyDate) {
    // 今日の県は「同じ条件でこの点に挑戦して」が伝わる文にする
    const where = result.regionName ?? result.stageTitle;
    const rank = result.dailyRank ? `（${formatDailyRank(result.dailyRank.rank, result.dailyRank.total)}）` : '';
    return (
      `【${result.dailyLabel ?? '今日の県'}】${where}で ${result.score.toLocaleString('ja-JP')} 点！${rank}\n` +
      `今日はみんな同じ順番で料理が落ちてきます。この点に挑戦してみて👇\n` +
      `最高到達: ${result.maxItemName}\n` +
      comboLine(result) +
      `\n${hashtags(result.stageTitle)}\n${shareUrl(result, medium)}`
    );
  }
  const recordPart = result.isNewRecord ? '🎉 ハイスコア更新！\n' : '';
  const dailyPart = result.dailyLabel ? `【${result.dailyLabel}】みんな同じ順番で落ちてくる日\n` : '';
  return (
    `${dailyPart}${recordPart}${result.stageTitle} で ${result.score.toLocaleString('ja-JP')} 点！\n` +
    `ハイスコア: ${result.highScore.toLocaleString('ja-JP')}点\n` +
    `最高到達: ${result.maxItemName}\n` +
    comboLine(result) +
    `\n${hashtags(result.stageTitle)}\n遊ぶ 👇\n${shareUrl(result, medium)}`
  );
}

function buildShareUrl(
  platform: 'twitter' | 'line' | 'facebook',
  text: string,
  url: string,
): string {
  const encoded = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);
  switch (platform) {
    case 'twitter':
      return `https://twitter.com/intent/tweet?text=${encoded}`;
    case 'line':
      // URL だけでなく本文（スコア・到達した料理）も送る。本文の末尾に URL が入っている
      return `https://line.me/R/share?text=${encoded}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    default:
      return '#';
  }
}

export function shareNative(result: ShareableResult): Promise<boolean> {
  const text = buildShareText(result, 'native');
  const shareData = {
    title: result.stageTitle,
    text,
    url: shareUrl(result, 'native'),
  } as ShareData;

  if (isNativeApp()) return nativeShareText(shareData);
  // canShare は未実装のブラウザがある。存在する場合だけ判定に使う。
  if (typeof navigator.share !== 'function') return Promise.resolve(false);
  if (typeof navigator.canShare === 'function' && !navigator.canShare(shareData)) {
    return Promise.resolve(false);
  }
  return navigator
    .share(shareData)
    .then(() => true)
    .catch(() => false);
}

export function shareTo(platform: 'twitter' | 'line' | 'facebook', result: ShareableResult): void {
  const medium: ShareMedium = platform === 'twitter' ? 'x' : platform;
  const text = buildShareText(result, medium);
  const url = buildShareUrl(platform, text, shareUrl(result, medium));
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function copyScoreText(result: ShareableResult): Promise<boolean> {
  const text = buildShareText(result, 'copy');
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // フォールバック
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.append(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    textarea.remove();
    return ok;
  } catch {
    return false;
  }
}

export function buildScoreCardSvg(result: ShareableResult, stage: StageDefinition): string {
  const width = 1200;
  const height = 630;
  const primary = stage.theme.primaryColor ?? '#c84a3d';
  const bg = stage.theme.backgroundColor ?? '#2b1a17';

  return `
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#3d2520"/>
      <stop offset="1" stop-color="${bg}"/>
    </linearGradient>
    <filter id="shadow">
      <feDropShadow dx="0" dy="8" stdDeviation="8" flood-color="#000" flood-opacity="0.35"/>
    </filter>
  </defs>
  <rect width="${width}" height="${height}" fill="url(#bg)"/>
  <rect x="60" y="60" width="1080" height="510" rx="48" fill="#fff8f0" opacity="0.08"/>
  <text x="600" y="130" text-anchor="middle" fill="#f8e8d8" font-size="42" font-family="sans-serif" font-weight="700">${escapeXml(result.stageTitle)}</text>
  <text x="600" y="240" text-anchor="middle" fill="${primary}" font-size="120" font-family="sans-serif" font-weight="900" filter="url(#shadow)">${result.score.toLocaleString('ja-JP')}</text>
  <text x="600" y="310" text-anchor="middle" fill="#f8e8d8" font-size="40" font-family="sans-serif">SCORE</text>
  <text x="600" y="400" text-anchor="middle" fill="#e8c9a8" font-size="34" font-family="sans-serif">ハイスコア ${result.highScore.toLocaleString('ja-JP')} 点</text>
  <text x="600" y="460" text-anchor="middle" fill="#e8c9a8" font-size="34" font-family="sans-serif">最高到達 ${escapeXml(result.maxItemName)}</text>
  ${result.isNewRecord ? `<text x="600" y="550" text-anchor="middle" fill="#ffd700" font-size="48" font-family="sans-serif" font-weight="800">🎉 ハイスコア更新！</text>` : `<text x="600" y="550" text-anchor="middle" fill="#a08070" font-size="34" font-family="sans-serif">${escapeXml(hashtag(result.stageTitle))}</text>`}
</svg>
  `.trim();
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * ストーリー／リール向けの縦長スコアカード（1080x1920, 9:16）。
 *
 * Instagram のストーリーや TikTok は「リンク」ではなく「画像・動画」を受け取るため、
 * 共有シートへ渡すのはこちらを使う。
 */
export function buildStoryCardSvg(result: ShareableResult, stage: StageDefinition): string {
  const width = 1080;
  const height = 1920;
  const primary = stage.theme.primaryColor ?? '#c84a3d';
  const bg = stage.theme.backgroundColor ?? '#2b1a17';

  return `
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#3d2520"/>
      <stop offset="1" stop-color="${bg}"/>
    </linearGradient>
    <filter id="shadow">
      <feDropShadow dx="0" dy="10" stdDeviation="10" flood-color="#000" flood-opacity="0.4"/>
    </filter>
  </defs>
  <rect width="${width}" height="${height}" fill="url(#bg)"/>
  <rect x="70" y="360" width="940" height="1200" rx="56" fill="#fff8f0" opacity="0.08"/>
  <text x="540" y="300" text-anchor="middle" fill="#f8e8d8" font-size="56" font-family="sans-serif" font-weight="700">${escapeXml(result.stageTitle)}</text>
  <text x="540" y="560" text-anchor="middle" fill="#e8c9a8" font-size="44" font-family="sans-serif">SCORE</text>
  <text x="540" y="760" text-anchor="middle" fill="${primary}" font-size="180" font-family="sans-serif" font-weight="900" filter="url(#shadow)">${result.score.toLocaleString('ja-JP')}</text>
  <text x="540" y="960" text-anchor="middle" fill="#e8c9a8" font-size="46" font-family="sans-serif">ハイスコア ${result.highScore.toLocaleString('ja-JP')} 点</text>
  <text x="540" y="1050" text-anchor="middle" fill="#e8c9a8" font-size="46" font-family="sans-serif">最高到達 ${escapeXml(result.maxItemName)}</text>
  ${
    result.isNewRecord
      ? `<text x="540" y="1220" text-anchor="middle" fill="#ffd700" font-size="64" font-family="sans-serif" font-weight="800">ハイスコア更新！</text>`
      : ''
  }
  <text x="540" y="1480" text-anchor="middle" fill="#f8e8d8" font-size="52" font-family="sans-serif" font-weight="700">${escapeXml(hashtag(result.stageTitle))}</text>
  <text x="540" y="1700" text-anchor="middle" fill="#a08070" font-size="36" font-family="sans-serif">${escapeXml(publicHost())}</text>
</svg>
  `.trim();
}

/** SVG 文字列を PNG の Blob へラスタライズする。失敗したら null。 */
async function renderSvgToPngBlob(
  svg: string,
  width: number,
  height: number,
): Promise<Blob | null> {
  try {
    const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    return await new Promise<Blob | null>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        URL.revokeObjectURL(url);
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob), 'image/png');
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  } catch {
    return null;
  }
}

/**
 * 画像ファイルを OS の共有シートへ渡せるか。
 *
 * Instagram と TikTok には Web から投稿する URL が存在しないため、
 * この経路（共有シートに並ぶアプリへ画像を渡す）が唯一の導線になる。
 */
export function canShareImage(): boolean {
  if (isNativeApp()) return true;
  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') {
    return false;
  }
  try {
    const probe = new File(['probe'], 'probe.png', { type: 'image/png' });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

/**
 * 縦長スコアカードを PNG にして OS の共有シートへ渡す。
 * 共有シートには Instagram / TikTok / LINE など、端末に入っているアプリが並ぶ。
 */
export async function shareScoreImage(
  result: ShareableResult,
  stage: StageDefinition,
  visual?: ShareVisual,
): Promise<boolean> {
  const blob = visual
    ? await renderShareCard(result, visual)
    : await renderSvgToPngBlob(buildStoryCardSvg(result, stage), 1080, 1920);
  if (!blob) return false;

  const fileName = `gotochi-meshi-merge-${stage.regionId}-${result.score}.png`;
  if (isNativeApp()) {
    return nativeShareFile(blob, fileName, { title: result.stageTitle, text: buildShareText(result, 'image_share') });
  }
  const file = new File([blob], fileName, {
    type: 'image/png',
  });
  const shareData: ShareData = {
    files: [file],
    title: result.stageTitle,
    text: buildShareText(result, 'image_share'),
  };

  if (typeof navigator.share !== 'function') return false;
  if (typeof navigator.canShare === 'function' && !navigator.canShare(shareData)) return false;

  try {
    await navigator.share(shareData);
    return true;
  } catch {
    return false;
  }
}

/** 横長スコアカード（1200x630）を PNG でダウンロードする。 */
export async function downloadScoreCard(
  result: ShareableResult,
  stage: StageDefinition,
  visual?: ShareVisual,
): Promise<boolean> {
  // 料理と景色の入った縦長カードを優先する（SNS のストーリーにそのまま貼れる）
  const blob = visual
    ? await renderShareCard(result, visual)
    : await renderSvgToPngBlob(buildScoreCardSvg(result, stage), 1200, 630);
  if (!blob) return false;

  // WebView では <a download> が動かない。共有シートの「画像を保存」に任せる
  if (isNativeApp()) {
    return nativeShareFile(blob, `gotochi-meshi-merge-${stage.regionId}-${result.score}.png`);
  }

  const downloadUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = `gotochi-meshi-merge-${stage.regionId}-${result.score}.png`;
  a.click();
  URL.revokeObjectURL(downloadUrl);
  return true;
}
